import React, { useState } from 'react'
import styled from 'styled-components'
import { cronosTokens } from '@pancakeswap/tokens'
import { Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import getBoredCandyNftImageUrl from 'utils/getNftImageUrl'
import { getBalanceNumber } from 'utils/formatBalance'
import { LockV2Config, LockV2Data } from '../../../types'
import { getLockApr } from '../../../hooks/helpers'
import useLockReward from '../../../hooks/v2/useLockReward'
import NftItem from '../../Nft/NftItem'
import EarlyClaimFee from './EarlyClaimFee'
import AvailableReward from './AvailableReward'
import EarlyUnlockFee from './EarlyUnlockFee'

const StyledLockCardDetails = styled.div`
  margin: 16px 12px 0;
`

const Item = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  margin-bottom: 4px;
`

const Notion = styled.div`
  // color: ${({ theme }) => theme.colors.secondary};
  font-size: 16px;
  font-weight: bold;
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  padding: 6px 2px;
  margin-bottom: 8px;
`

const Display = styled(Text) <{ subHeader?: boolean }>`
  font-size: ${({ subHeader }) => subHeader ? '13px' : '15px'};
  margin-left: ${({ subHeader }) => subHeader ? '8px' : '0px'};
  flex: 1;
`

const NftRow = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    height: 8px;
  }
`

const LockCardDetails: React.FC<{
  data: LockV2Data;
  config: LockV2Config;
  epochTime: number
}> = ({ data, config, epochTime }) => {
  const { t } = useTranslation()
  const candyToken = cronosTokens.candy

  const {
    id,
    lockAt,
    duration,
    lastRewardAt,
    tokenAmount,
    listed,
    listPrice,
    nftIds
  } = data
  const { penaltyStart, penaltyDelta, aprRates, boostRates } = config
  const { reward, penaltyPercent, penaltyAppliedReward } = useLockReward(id)

  return (
    <>
      <StyledLockCardDetails>
        {
          listed && (
            <Notion>
              {
                t('Listed for the sale in %price% CANDY', {
                  price: getBalanceNumber(listPrice, candyToken.decimals).toLocaleString()
                })
              }
            </Notion>
          )
        }
        <Item>
          <Display>{t('Lock Start Date')}</Display>
          <Text>
            {`${new Date(lockAt * 1000).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            })} (UTC)`
            }
          </Text>
        </Item>
        <Item>
          <Display>{t('Lock End Date')}</Display>
          <Text>
            {`${new Date((lockAt + duration) * 1000).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            })} (UTC)`
            }
          </Text>
        </Item>
        <Item>
          <Display>{t('Lock Amount')}</Display>
          <Text>{getBalanceNumber(tokenAmount, candyToken.decimals)
            .toLocaleString('en-US', { maximumFractionDigits: 3 })} {candyToken.symbol}</Text>
        </Item>
        <Item>
          <Display>{t('Boosting Nfts')}</Display>
          {nftIds.length === 0 ? (
            <Text>No NFTs Locked</Text>
          ) : <></>}
        </Item>
        {nftIds.length > 0 && (
          <NftRow>
            {nftIds.map((nft) => (
              <NftItem key={`Boost NFT item - ${nft}`} id={nft} image={getBoredCandyNftImageUrl(nft)} />
            ))}
          </NftRow>
        )}
        <Item>
          <Display>{t('APR')}</Display>
          <Text>{getLockApr(aprRates, boostRates, duration, nftIds)} %</Text>
        </Item>
        <Item>
          <EarlyClaimFee
            penaltyStart={penaltyStart}
            penaltyDelta={penaltyDelta}
            penaltyPercent={penaltyPercent} />
        </Item>
        <Item>
          <Display>{t('Available Reward')}</Display>
          <AvailableReward
            lastRewardAt={lastRewardAt}
            penaltyAppliedReward={penaltyAppliedReward}
            penaltyPercent={penaltyPercent}
            reward={reward}
          />
        </Item>
        <Item>
          <EarlyUnlockFee
            duration={duration}
            epochTime={epochTime}
            lockAt={lockAt} />
        </Item>
      </StyledLockCardDetails>
    </>
  )
}

export default LockCardDetails
